// Shared installer data + platform detection for the public download surfaces
// (the landing's downloads section and the /download page). Installer URLs are
// env-driven (VITE_WAYTIDE_*_INSTALLER_URL), baked into the build from repo
// secrets; a missing URL renders as "pending"/"coming soon".

export type InstallerId = "windows" | "macos" | "linux";

export type DesktopInstallerOption = {
  id: InstallerId;
  label: string;
  detail: string;
  href?: string;
};

export type MobileAppOption = {
  id: string;
  label: string;
  detail: string;
  href?: string;
};

const installerEnv = import.meta.env as Record<string, string | undefined>;

export function readInstallerUrl(key: string): string | undefined {
  const value = installerEnv[key]?.trim();
  return value ? value : undefined;
}

export function readUserAgent(): string {
  return typeof navigator === "undefined" ? "" : navigator.userAgent;
}

export const DEFAULT_INSTALLERS: readonly DesktopInstallerOption[] = [
  {
    id: "macos",
    label: "macOS",
    detail: "Universal — Apple Silicon + Intel.",
    href: readInstallerUrl("VITE_WAYTIDE_MACOS_INSTALLER_URL"),
  },
  {
    id: "windows",
    label: "Windows",
    detail: "Windows 10 and 11.",
    href: readInstallerUrl("VITE_WAYTIDE_WINDOWS_INSTALLER_URL"),
  },
  {
    id: "linux",
    label: "Linux",
    detail: "AppImage — portable, any distro.",
    href: readInstallerUrl("VITE_WAYTIDE_LINUX_INSTALLER_URL"),
  },
];

// Mobile is secondary to the desktop app (the input), env-driven like the
// desktop installers — "Coming soon" until a store listing is published.
export const MOBILE_APPS: readonly MobileAppOption[] = [
  {
    id: "ios",
    label: "iPhone — App Store",
    detail: "The native iOS app, once it's published.",
    href: readInstallerUrl("VITE_WAYTIDE_IOS_APP_URL"),
  },
  {
    id: "android",
    label: "Android — Google Play",
    detail: "The native Android app, once it's published.",
    href: readInstallerUrl("VITE_WAYTIDE_ANDROID_APP_URL"),
  },
];

/** The installer to promote as the primary download for this visitor — the one
 * matching their OS (by user-agent), falling back through the others. Null when
 * none has a configured URL. Pure. */
export function selectPreferredInstaller(
  installers: readonly DesktopInstallerOption[],
  userAgent: string,
): DesktopInstallerOption | null {
  const available = installers.filter((installer) => installer.href);
  if (available.length === 0) return null;

  const normalizedAgent = userAgent.toLowerCase();
  const preferredOrder: InstallerId[] = normalizedAgent.includes("windows")
    ? ["windows", "linux", "macos"]
    : normalizedAgent.includes("mac")
      ? ["macos", "windows", "linux"]
      : normalizedAgent.includes("linux")
        ? ["linux", "windows", "macos"]
        : ["macos", "windows", "linux"];

  for (const id of preferredOrder) {
    const match = available.find((installer) => installer.id === id);
    if (match) return match;
  }
  return available[0] ?? null;
}

export function platformLabel(id: InstallerId): string {
  if (id === "macos") return "macOS";
  return id[0].toUpperCase() + id.slice(1);
}
